import type { IHeaderComp, IHeaderParams } from 'ag-grid-community'
import { iconElement, iconVars } from '../utils/dom'

// Custom header parameters extending AG Grid's base params
type CustomHeaderParams = IHeaderParams & {
  readonly icon?: string
}

/** Custom header component class implementing AG Grid's IHeaderComp interface */
export class HeaderComponent implements IHeaderComp {
  private eGui: HTMLDivElement | undefined
  private eText: HTMLSpanElement | undefined
  private eIcon: HTMLElement | undefined

  /** Initialize the header component with AG Grid parameters */
  init(params: CustomHeaderParams): void {
    // Create main container with flex layout
    this.eGui = document.createElement('div')
    this.eGui.classList.add('flex', 'min-w-0', 'items-center', 'gap-2')

    // Create text node for display name
    const textNode = document.createElement('span')
    textNode.classList.add('truncate')
    this.eText = textNode
    textNode.textContent = params.displayName

    // Add icon if provided — plain light-DOM markup, no <facade-icon> upgrade.
    if (params.icon) {
      this.eIcon = iconElement(params.icon)
      this.eGui.appendChild(this.eIcon)
    }

    // Add text node to container
    this.eGui.appendChild(textNode)
  }

  /** Return the DOM element for AG Grid */
  getGui(): HTMLDivElement {
    return this.eGui!
  }

  /** Refresh the header when parameters change */
  refresh(params: CustomHeaderParams): boolean {
    if (!this.eText) {
      return false
    }

    // Update text content
    this.eText.textContent = params.displayName

    // Update icon with proper state management
    this.updateIcon(params.icon)

    return true
  }

  /** Update icon with proper element lifecycle management */
  private updateIcon(iconName?: string): void {
    if (!(this.eGui && this.eText)) {
      return
    }

    if (iconName) {
      if (this.eIcon) {
        // Update existing icon's CSS-var contract in place
        const i = this.eIcon.firstElementChild as HTMLElement
        const vars = iconVars(iconName)
        i.style.setProperty('--icon-size', vars['--icon-size'])
        i.style.setProperty('--icon-url', vars['--icon-url'])
      } else {
        // Create and insert new icon before text
        this.eIcon = iconElement(iconName)
        this.eGui.insertBefore(this.eIcon, this.eText)
      }
    } else if (this.eIcon) {
      // Clean up icon when no longer needed
      this.eIcon.remove()
      this.eIcon = undefined
    }
  }

  /** Cleanup method for proper lifecycle management */
  destroy(): void {
    this.eIcon = undefined
    this.eText = undefined
    this.eGui = undefined
  }
}

// Export types for external use
export type { CustomHeaderParams }
